/* eslint-disable object-curly-newline,prefer-destructuring,no-unused-vars,no-extra-parens */
import { asyncPipeSpreadIf, def } from 'lib/util'
import find from 'lib/sequence/component/children/find'
import asObject from 'lib/sequence/component/children/asObject'
import { get, set } from 'lib/sequence/component/state/value'
import { show } from 'lib/sequence/component/state/hidden'
import { checked } from 'lib/sequence/component/state/checked'
import { settings } from 'app/_shared/session'
import initOrganisationType from 'app/_shared/action/partial/initOrganisationType'
import processRequestType from 'app/_shared/action/partial/processRequestType'
import toggleDeviceInstalledAt from 'app/_shared/action/partial/toggleDeviceInstalledAt'
import initSerialImage from 'app/_shared/action/partial/initSerialImage'
import initSerialField from 'app/_shared/action/partial/initSerialField'
import listLanguages from 'app/_shared/events/collection/listLanguages'
import listCountries from 'app/_shared/events/collection/listCountries'
import initAddress from 'app/_shared/action/address/init'
import displayState from 'app/_shared/events/address/displayState'
import displayHouseNumber from 'app/_shared/events/address/displayHouseNumber'
import stepper from 'app/_shared/action/stepper'
import personForm from './register/personal'
import organisationForm from './register/organisation'
import tosForm from './register/tos'
import finish from 'app/_shared/action/partial/finish'

/**
 * If the URL has a third parameter, displays the form in its extended variant.
 *
 * @param module the current module composition object
 */
const toggleExtendedForm = (module: Gaia.AppModule.Spec) => async (components, ...args) => {
  const { [G.REF]: ref, key = ref } = args[0] || {}
  const showExtendedForm = key || settings.forceExtendedRegistration

  showExtendedForm && show(components.requestTypeForm)

  return [components, ...args]
}

/**
 * Checks whether the device address form is shown by inspecting the `checked`
 * state of the `sameLocation` component.
 *
 * @param module the current module composition object
 */
const isFormShown = (module: Gaia.AppModule.Spec): boolean => {
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]
  const { sameLocation } = find(actionComponent)

  return sameLocation[G.STATE]?.checked === false
}

/**
 * Initializes the language field with the current language.
 *
 * @param module the current module composition object
 */
export const initLanguage = (module: Gaia.AppModule.Spec) => async (components, ...args) => {
  const { lang } = components

  if (!get(lang)) {
    const iso = module[G.ADAPTER][G.INTL].get().language
    const languages = listLanguages(module, null, undefined)
    const currentLanguage = languages.find(language => language.key === iso)
    set(lang, [currentLanguage])
  }

  return [components, ...args]
}

/**
 * If provided, sets the username passed as argument as the value of the account field.
 *
 * @param components the find component sequence initialized with the action's component
 * @param args       the action's arguments
 */
export const initUsername = async (components, ...args) => {
  const { username } = args[0] || {}

  if (username) {
    const { account } = components
    !def(get(account)) && set(account, username)
  }

  return [components, ...args]
}

/**
 * Obtains the list of translated countries and sets the initial selection.
 *
 * @param module the current module composition object
 */
export const initCountry = (module: Gaia.AppModule.Spec) => async (components, ...args) => {
  const model = module[G.MODEL]
  const { country, deviceInstalledAt, sameLocation } = components
  const { address: deviceAddress } = deviceInstalledAt
    ? asObject(deviceInstalledAt?.[G.CHILDREN])
    : {}
  const { country: deviceCountry } = deviceAddress
    ? asObject(deviceAddress?.[G.CHILDREN])
    : {}

  if (!def(get(country))) {
    try {
      const countries = listCountries(module, null, null)
      const { country_code: countryCode } = await model[G.ADAPTER][G.HTTP].get({
        url: '/api/v1/public/ipinfo',
      }, { middleware: ({ persistence }) => [persistence] })

      const selectedCountry = countries.filter(i => i.key === countryCode)
      countryCode && set(country, selectedCountry)
    } catch (ignored) {}
  }

  if (PLATFORM === 'web') {
    // Copy value from country to deviceCountry if not set already
    !checked(sameLocation)
    && def(get(country))
    && !def(get(deviceCountry))
    && set(deviceCountry, get(country))
  }

  return [components, ...args]
}

const initOrganisationAddress = initAddress(
  { component: 'country' },
  { attribute: 'organisation' },
)

const initDeviceAddress = initAddress(
  { parent: 'deviceInstalledAt', component: 'country' },
  { parent: 'itemInstalledAt', attribute: 'request' },
)

/**
 * Gathers the actions for the stepper.
 *
 * @param module the current module composition object
 */
const initActions = (module: Gaia.AppModule.Spec) => component => async (...args) => [
  { personForm, organisationForm, tosForm }, args,
]

/**
 * Initializes the stepper.
 *
 * @param module the current module composition object
 */
const initStepper = (module: Gaia.AppModule.Spec) => component => async (actions, ...args) => (
  await stepper(module)(component)({ actions, ...args })
)

/**
 * Register action
 *
 * Initializes and controls the action's business logic.
 *
 * @param module the current module composition object
 */
export default (module: Gaia.AppModule.Spec) => component => async (...args) => (
  asyncPipeSpreadIf(() => !settings.externalPortalUrl)(
    initCountry(module),
    initOrganisationAddress(
      displayState('state', 'country'),
      displayHouseNumber('number', 'street'),
    )(module),
    initUsername,
    initLanguage(module),
    initOrganisationType,

    asyncPipeSpreadIf(() => PLATFORM === 'web')(
      processRequestType(module, true),
      toggleExtendedForm(module),
      toggleDeviceInstalledAt,
      asyncPipeSpreadIf(() => isFormShown(module))(
        initDeviceAddress(
          displayState('state', 'country'),
          displayHouseNumber('number', 'street'),
        )(module),
      ),
      initSerialField(module),
      initSerialImage(module),
    ),

    asyncPipeSpreadIf(() => PLATFORM === 'mobile')(
      initActions(module)(component),
      initStepper(module)(component),
    ),
    finish,
  )(find(component), ...args)
)
